<template>
    <div class="quickpay-marketing-preferences">
        <div
            class="quickpay-marketing-preferences__header"
            @click="toggleAccordion"
        >
            <span class="quickpay-marketing-preferences__title typographies-paragraph-medium-semibold">
                {{ $t.__('quickpay.QuickpayMarketingPreferences.title') }}
            </span>
            <CompassIcon
                icon="caret-right-fill"
                size="extra-small"
                class="quickpay-marketing-preferences__caret"
                :class="{ 'quickpay-marketing-preferences__caret--open': isOpen }"
            />
        </div>
        <div
            class="quickpay-marketing-preferences__content"
            :class="{ 'quickpay-marketing-preferences__content--open': isOpen }"
            @transitionend="onContentTransitionEnd"
        >
            <div class="quickpay-marketing-preferences__content-wrapper">
                <CompassAlert
                    v-if="showSuccessAlert && massmailing.double_opt_in"
                    type="success"
                    :description="$t.__('quickpay.QuickpayMarketingPreferences.successMessage')"
                    :showIcon="true"
                    :showClose="false"
                    class="quickpay-marketing-preferences__alert"
                />
                <div class="quickpay-marketing-preferences__campaigns">
                    <CompassCheckbox
                        v-for="campaign in campaigns"
                        :key="campaign.id"
                        :modelValue="campaign.id"
                        :checked="campaign.subscribed"
                        :label="campaign.label"
                        :showLabel="true"
                        class="quickpay-marketing-preferences__checkbox"
                        @change="(value) => handleCampaignChange(campaign.id, value)"
                    />
                </div>
                <p
                    v-if="showUpdatedMessage && !massmailing.double_opt_in"
                    class="quickpay-marketing-preferences__updated typographies-paragraph-small-regular"
                >
                    <CompassIcon icon="check" size="extra-small" />
                    {{ $t.__('quickpay.QuickpayMarketingPreferences.updatedMessage') }}
                </p>
                <CompassButton
                    v-if="massmailing.double_opt_in"
                    :label="$t.__('quickpay.QuickpayMarketingPreferences.confirmButton')"
                    type="tertiary"
                    :loading="isLoading"
                    :disabled="!hasChanges"
                    class="quickpay-marketing-preferences__button"
                    @click="handleConfirm"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        massmailing: {
            type: Object,
            default: null,
        },
        hash: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isOpen: false,
            isLoading: false,
            showSuccessAlert: false,
            showUpdatedMessage: false,
            campaigns: [],
            originalSubscriptions: {},
        };
    },
    computed: {
        hasChanges() {
            return this.campaigns.some(
                (campaign) => campaign.subscribed !== this.originalSubscriptions[campaign.id]
            );
        },
    },
    watch: {
        massmailing: {
            immediate: true,
            handler(newVal) {
                if (newVal?.types) {
                    this.initializeCampaigns(newVal.types);
                }
            },
        },
    },
    methods: {
        initializeCampaigns(types) {
            // Invert unsubscribed to subscribed (false = subscribed, true = unsubscribed)
            this.campaigns = types.map((type) => ({
                id: type.id,
                label: type.label,
                subscribed: !type.unsubscribed,
            }));
            // Store original state for comparison
            this.originalSubscriptions = {};
            for (const campaign of this.campaigns) {
                this.originalSubscriptions[campaign.id] = campaign.subscribed;
            }
        },
        toggleAccordion() {
            this.isOpen = !this.isOpen;
        },
        onContentTransitionEnd(event) {
            // Only handle the max-height transition (not opacity) and only when closing
            if (event.propertyName === 'max-height' && !this.isOpen) {
                this.showSuccessAlert = false;
                this.showUpdatedMessage = false;
            }
        },
        async handleCampaignChange(campaignId, subscribed) {
            const campaign = this.campaigns.find((campaign) => campaign.id === campaignId);
            if (campaign) {
                campaign.subscribed = subscribed;
            }
            // If not double opt-in, save immediately using /quickpay/optin (toggle behavior)
            if (!this.massmailing.double_opt_in) {
                try {
                    await this.saveSingleCampaign(campaignId, subscribed);
                    this.showUpdatedMessage = true;
                } catch {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                }
            }
        },
        async handleConfirm() {
            try {
                await this.savePreferencesRgpd();
                this.showSuccessAlert = true;
                // Reset original subscriptions to current state
                for (const campaign of this.campaigns) {
                    this.originalSubscriptions[campaign.id] = campaign.subscribed;
                }
            } catch {
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$t.__('common.labels.toast.error'),
                });
            }
        },
        async savePreferencesRgpd() {
            this.isLoading = true;
            const subscribed = this.campaigns
                .filter((campaign) => campaign.subscribed)
                .map((campaign) => campaign.id);
            const unsubscribed = this.campaigns
                .filter((campaign) => !campaign.subscribed)
                .map((campaign) => campaign.id);
            try {
                await this.$ajax.post(
                    '/quickpay/rgpdoptin',
                    {
                    },
                    {
                        hash: this.hash,
                        subscribe: subscribed.join(','),
                        unsubscribe: unsubscribed.join(','),
                    }
                );
            } finally {
                this.isLoading = false;
            }
        },
        async saveSingleCampaign(campaignId, subscribed) {
            this.isLoading = true;
            try {
                await this.$ajax.post(
                    '/quickpay/optin',
                    {
                    },
                    {
                        hash: this.hash,
                        campaign: campaignId,
                        unsubscribeAll: campaignId === 'all' && subscribed ? 'N' : 'Y',
                        useCampaignAll: campaignId === 'all' ? 'Y' : 'N',
                    }
                );
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.quickpay-marketing-preferences {
    width: 100%;
    background: var(--colors-common-background-default);
    border: 1px solid var(--colors-common-border-default);
    border-radius: var(--spacing-1);
    overflow: hidden;
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-1) var(--spacing-3);
        cursor: pointer;
    }
    &__title {
        margin: 0;
    }
    &__caret {
        transition: transform 300ms ease-in-out;
        &--open {
            transform: rotate(90deg);
        }
    }
    &__content {
        max-height: 0;
        overflow: hidden;
        opacity: 0;
        transition: max-height 300ms ease-in-out, opacity 150ms ease-in-out;
        &--open {
            max-height: 500px;
            opacity: 1;
        }
    }
    &__content-wrapper {
        padding: var(--spacing-1) var(--spacing-3) var(--spacing-2);
    }
    &__alert {
        margin-bottom: var(--spacing-2);
    }
    &__campaigns {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }
    &__checkbox {
        width: 100%;
    }
    &__button {
        margin-top: var(--spacing-2);
        width: 100%;
    }
    &__updated {
        display: flex;
        align-items: center;
        gap: var(--spacing-0);
        margin: var(--spacing-1) 0 0;
        color: var(--colors-common-text-subtle-default);
    }
}
</style>
